/**
 * Display helpers — date formatting, money, channel labels, relative time, etc.
 * Light wrappers around Intl APIs so we don't need a date library.
 */

export function formatRelative(date: string | Date | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const now = Date.now();
  const ms = now - d.getTime();
  // Future timestamps render as "in 3h" rather than negative-second nonsense.
  if (ms < 0) return formatTimeUntil(d);
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  const hr  = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (sec < 5)   return "just now";
  if (sec < 60)  return `${sec}s ago`;
  if (min < 60)  return `${min}m ago`;
  if (hr  < 24)  return `${hr}h ago`;
  if (day < 7)   return `${day}d ago`;
  if (day < 30)  return `${Math.floor(day / 7)}w ago`;
  if (day < 365) return formatDate(d, { month: "short", day: "numeric" });
  return formatDate(d, { year: "numeric", month: "short", day: "numeric" });
}

export function formatTimeUntil(date: string | Date | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return "now";
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  const hr  = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (sec < 60)  return `in ${sec}s`;
  if (min < 60)  return `in ${min}m`;
  if (hr  < 24)  return `in ${hr}h`;
  if (day < 7)   return `in ${day}d`;
  if (day < 30)  return `in ${Math.floor(day / 7)}w`;
  return formatDate(d, { month: "short", day: "numeric" });
}

export function formatDate(date: string | Date | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString(undefined, options ?? { month: "short", day: "numeric", year: "numeric" });
}

export function formatTime(date: string | Date | null | undefined): string {
  return formatDate(date, { hour: "numeric", minute: "2-digit" });
}

/**
 * Format a Date as a YYYY-MM-DD string in the LOCAL timezone — never UTC.
 *
 * This is the right helper any time you're using a date string as a key
 * (calendar grid, day buckets, "today" comparisons, ?date= query params).
 * Using `d.toISOString().slice(0,10)` for this silently produces the UTC
 * date, so a 9pm event in PST gets bucketed onto the next day.
 */
export function formatLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatDateTime(date: string | Date | null | undefined): string {
  return formatDate(date, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * Format a cents-denominated amount as a currency string.
 *
 * Defaults to 2 decimal places — anything else silently corrupts invoice
 * totals (a $99.50 invoice rendered as "$100" is a real bug, not a style
 * choice). For dashboard tiles where you actually want the rounded
 * approximate, use formatMoneyApprox.
 */
export function formatMoney(cents: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

/** Like formatMoney but rounds to whole units — for compact dashboard tiles. */
export function formatMoneyApprox(cents: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(0)}`;
  }
}

export function formatPhone(phone: string | null): string {
  if (!phone) return "";
  // Display as +1 (415) 555-1234 or fallback to original
  const digits = phone.replace(/[^\d+]/g, "");
  if (/^\+1\d{10}$/.test(digits)) {
    return `+1 (${digits.slice(2, 5)}) ${digits.slice(5, 8)}-${digits.slice(8)}`;
  }
  return phone;
}

// Channel labels live in lib/channels.ts (single source of truth across
// the inbox filter pills, consent panel, channel-icon picker, message
// provenance, etc.). Re-exported here for backwards compatibility with
// existing `from "@/lib/format"` imports.
export { CHANNEL_LABELS, channelLabel } from "./channels";

export function getInitials(name: string | null | undefined): string {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function fullName(c: { first_name?: string | null; last_name?: string | null; full_name?: string | null; email?: string | null; phone?: string | null } | null | undefined): string {
  if (!c) return "Unknown";
  if (c.full_name) return c.full_name;
  const n = [c.first_name, c.last_name].filter(Boolean).join(" ");
  // A caller we only know by number reads better as the number than "Unknown".
  return n || c.email || (c.phone ? formatPhone(c.phone) : "") || "Unknown";
}
